import * as Actions from '../actions/addressActions';
import Address from '../../entities/address';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type AddressAction = { 
    type: string; 
    address: Address | null; 
    addressId: string | Address; 
    partial: boolean; 
    lastFetch: number; 
    addresses: Address[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Address> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Address[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Address> | null, firmId: string, userId: string, updates?: Address[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'address', 'addresses', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Address>} data
 * @param {Address[]} [updates]
 * @returns {ArrayMap<Address>}
 */
function updateStorage(data: ArrayMap<Address>, updates?: Address[]): ArrayMap<Address> {
	if (logging) console.debug('⮂ addressReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Address[]} [updates]
 * @returns {ArrayMap<Address>}
 */
function updateStorageNoReturn(updates?: Address[]): void {
	if (logging) console.debug('⮂ addressReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Address[]} [data]
 * @returns {ArrayMap<Address>}
 */
function createState(data?: Address[] | ArrayMap<Address>): ArrayMap<Address> {
	if (!data) return ArrayMapRegistry.clone<Address>('address', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Address>('address', data.map(e => e));
    else return ArrayMapRegistry.clone<Address>('address', data);
}

function addresses(state: ArrayMap<Address>, action: AddressAction): ArrayMap<Address> {
	if (!state) state = createState();

	let st: ArrayMap<Address>;

	switch (action.type) {
		// action.address - the new value of address with id action.id
		// action.addressId - id of the target, used if new value is null
		case Actions.ADDRESS_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ addressReducer: FETCH_ONE_SUCCESS', action);

			if (action.address === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.addressId)) {
				st = state.clone();
				st.set(action.address);
				// state.clear();

				updateStorageNoReturn([action.address]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.addresses
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.addresses
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.addresses
			 !partial, !stateEmpty, fetchEmpty	-> D return addresses in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge addresses in state newer than those in action.addresses
		 */
		case Actions.ADDRESS_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.addresses.length === 0;
			const fetched = action.addresses.map(address => new Address({...address, persisted: true}));

			if (logging) console.debug(`⮂ addressReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} addresses with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ addressReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ addressReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Address> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Address[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(address => {
					const current = st.get(address.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && address.status === 'D') {
							// Remove it from state
							toUpdate.push(address);
							st.delete(address);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (address.addedOn > current.addedOn) {
								toUpdate.push(address);
								st.set(address);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (address.status !== 'D') {
							toUpdate.push(address);
							st.set(address);
						}
						else if (logging) console.debug('⮂ addressReducer: partialMerge: skipping deleted address not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ addressReducer: empty: addresses', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ addressReducer: fullMerge: addresses', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localAddress => {
                    // If this address has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localAddress.persisted) {
                        const fetchedAddress = st.get(localAddress.id);
                        if (!fetchedAddress || localAddress.addedOn > fetchedAddress.addedOn) st.set(localAddress);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.ADDRESS_FETCH_ONE_FAILURE:
			console.error('ADDRESS_FETCH_ONE_FAILURE', action.addressId, action.err);

			// No update to storage required
			return state;

		case Actions.ADDRESS_FETCH_FAILURE:
			console.error('ADDRESS_FETCH_FAILURE', action.addressId, action.err);

			// No update to storage required
			return state;

		case Actions.ADDRESS_RESTORE: {
		    let address = new Address({...action.address});
            st = state.clone();

            const existing = st.get(address.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (address.addedOn >= existing.addedOn) {
                    st.set(address);
                    return updateStorage(st, [address]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (address.status === 'D') return state;

                st.set(address);
                return updateStorage(st, [address]);
            }
        }
            
		case Actions.ADDRESS_SAVE: {
		    // console.debug('⮂ ADDRESS_SAVE reducer', action.address?.id);
		    
			// clone the address
			let address = new Address({...action.address});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new address in the state
			// If this is a create, add to the queue
			const _address = st.get(address.id);

			if (!_address) st.set(address);
			else {
				if (address.addedOn >= _address.addedOn) st.set(address);
			}

			return updateStorage(st, [address]);
		}

		case Actions.ADDRESS_SAVE_MANY: {
		    if (logging) console.debug('⮂ ADDRESS_SAVE_MANY reducer', action.addresses.map(address => address.id).join(','));
		    
			// clone the addresses
			let toProcess = action.addresses.map(address => new Address(address));

			st = state.clone();
			// state.clear();

			const updates: Address[] = [];

			toProcess.forEach(address => {
				// Add or update the new address in the state
				// If this is a create, add to the queue
				const existing = st.get(address.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (address.status !== 'D') {
						st.push(address);
						updates.push(address);
					}
					else if (logging) console.debug('⮂ ADDRESS_SAVE_MANY: skipping deleted address not in state');
				}
				else {
					if (address.addedOn >= existing.addedOn) {
						st.set(address);
						updates.push(address);
					}
					else if (logging) console.debug('⮂ Skipping, existing address had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ADDRESS_REMOVE: {
			// clone the address
			const address = new Address({...action.address});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(address);

			return updateStorage(st, [address]);
		}

		case Actions.ADDRESS_RESTORE_FAILURE:
		case Actions.ADDRESS_SAVE_FAILURE: {
		    console.warn('ADDRESS_SAVE_FAILURE reducer', action.address?.id);
		    if (!action.address?.id) {
				console.warn('ADDRESS_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const address = ensure(Address, action.address);
			if (!address) {
				console.error('Could not find address after call to save');
				return state;
			}

			st = state.clone();

			const _addressRef = state.get(address.id);

			if (_addressRef && (_addressRef.addedOn === address.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _address = new Address({..._addressRef});
				_address.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_address.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('address', _address).then(r => {
						if (r.isErr()) console.error(`⮂ addressReducer: failed to save address with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ addressReducer: saved address with fatalError info');
					}).catch(err => console.error(`⮂ addressReducer: failed to save address with fatalError info: ${err.message}`));

				st.set(_address);
			}

			return updateStorage(st);
		}

		case Actions.ADDRESS_SAVE_MANY_FAILURE: {
		    console.warn('ADDRESS_SAVE_MANY_FAILURE reducer', action.addresses.map(address => address.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.ADDRESS_RESTORE_SUCCESS:
		case Actions.ADDRESS_SAVE_SUCCESS: {
			if (!action.address?.id) {
				console.warn('ADDRESS_SAVE_SUCCESS reducer');
                return state;
			}

			const addressRef = state.get(action.address?.id);
			if (!addressRef) {
				console.warn('Could not find address after call to save');
                return state;
			}

			st = state.clone();

			if (action.address.addedOn >= addressRef.addedOn) {
				st.set(action.address);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const address = new Address({...addressRef, persisted: true});
				st.set(address);
			}

			return updateStorage(st, [action.address]);
		}

		case Actions.ADDRESS_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ ADDRESS_SAVE_MANY_SUCCESS reducer', action.addresses.map(address => address.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const addresses: Address[] = [];

			action.addresses.forEach(e => {
				const addressRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!addressRef) console.warn('Could not find address after call to save');
				else if (e.addedOn >= addressRef.addedOn) {
				    st.set(e);
				    addresses.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const address = new Address({...addressRef, persisted: true});
					st.set(address);
					addresses.push(address);
				}
			});

			return updateStorage(st, addresses);
		}

		case Actions.ADDRESS_REMOVE_FAILURE: {
		    console.warn('ADDRESS_REMOVE_FAILURE action', action.address?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.ADDRESS_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.ADDRESS_RESTORE_MANY: {
			if (logging) console.debug('⮂ ADDRESS_RESTORE_MANY reducer', action.addresses.map(address => address.id).join(','));

			const toProcess = action.addresses.map(address => new Address({...address}));
			st = state.clone();

			const updates: Address[] = [];

			toProcess.forEach(address => {
				const existing = st.get(address.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (address.addedOn >= existing.addedOn) {
						st.set(address);
						updates.push(address);
					}
					else if (logging) console.debug('⮂ ADDRESS_RESTORE_MANY: skipping older version of address');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (address.status !== 'D') {
						st.set(address);
						updates.push(address);
					}
					else if (logging) console.debug('⮂ ADDRESS_RESTORE_MANY: skipping deleted address not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ADDRESS_REMOVE_MANY: {
			if (logging) console.debug('⮂ ADDRESS_REMOVE_MANY reducer', action.addresses.map(address => address.id).join(','));

			const toProcess = action.addresses.map(address => new Address({...address}));
			st = state.clone();

			toProcess.forEach(address => {
				st.delete(address);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default addresses;